import type {
  KeyChannelBindingCascadeCapabilityImpact,
  KeyChannelBindingCascadeImpact,
  KeyChannelBindingCascadeSkuTargetImpact,
  ListKeyChannelBindingsResponse,
  SetKeyChannelBindingsRequest,
  SetKeyChannelBindingsResponse,
} from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from './client.ts';

export function listBindings(
  upstreamId: string,
  bucketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ListKeyChannelBindingsResponse> {
  return opsApiRequest<ListKeyChannelBindingsResponse>(
    `/v1/ops/upstreams/${upstreamId}/key-buckets/${bucketId}/channels`,
    { method: 'GET' },
    fetchImpl,
  );
}

export function setBindings(
  upstreamId: string,
  bucketId: string,
  body: SetKeyChannelBindingsRequest,
  fetchImpl: typeof fetch = fetch,
): Promise<SetKeyChannelBindingsResponse> {
  return opsApiRequest<SetKeyChannelBindingsResponse>(
    `/v1/ops/upstreams/${upstreamId}/key-buckets/${bucketId}/channels`,
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body,
    },
    fetchImpl,
  );
}

/**
 * Narrow a failed `setBindings` error into the structured cascade impact
 * carried by a 409 `CASCADE_CONFIRMATION_REQUIRED` envelope. Returns null
 * for every other error so callers fall through to normal toast handling.
 */
export function cascadeImpactFromError(err: unknown): KeyChannelBindingCascadeImpact | null {
  if (!(err instanceof OpsApiError) || err.code !== 'CASCADE_CONFIRMATION_REQUIRED') {
    return null;
  }
  const impact = err.details?.['cascade_impact'];
  if (!isRecord(impact)) {
    return null;
  }
  const capabilities = parseArray(impact['capabilities_marked_mismatch'], parseCapabilityImpact);
  const skuTargets = parseArray(impact['sku_targets_deleted'], parseSkuTargetImpact);
  if (capabilities === null || skuTargets === null) {
    return null;
  }
  return {
    capabilities_marked_mismatch: capabilities,
    sku_targets_deleted: skuTargets,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseArray<T>(
  value: unknown,
  parseItem: (item: Record<string, unknown>) => T | null,
): readonly T[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const items: T[] = [];
  for (const raw of value) {
    if (!isRecord(raw)) {
      return null;
    }
    const item = parseItem(raw);
    if (item === null) {
      return null;
    }
    items.push(item);
  }
  return items;
}

function parseCapabilityImpact(
  raw: Record<string, unknown>,
): KeyChannelBindingCascadeCapabilityImpact | null {
  const { id, channel_id, channel_slug, vendor, model, protocol, status } = raw;
  if (
    typeof id !== 'string' || typeof channel_id !== 'string' ||
    typeof channel_slug !== 'string' || typeof vendor !== 'string' ||
    typeof model !== 'string' || typeof protocol !== 'string' ||
    typeof status !== 'string'
  ) {
    return null;
  }
  return { id, channel_id, channel_slug, vendor, model, protocol, status };
}

function parseSkuTargetImpact(
  raw: Record<string, unknown>,
): KeyChannelBindingCascadeSkuTargetImpact | null {
  const {
    scope, id, sku_id, sku_name, vendor, model, capability_id,
    upstream_channel_id, upstream_channel_slug, enabled,
  } = raw;
  if (
    !['default', 'config', 'org', 'org_override'].includes(String(scope)) ||
    typeof id !== 'string' || typeof sku_id !== 'string' || typeof sku_name !== 'string' ||
    typeof vendor !== 'string' || typeof model !== 'string' ||
    (capability_id !== null && typeof capability_id !== 'string') ||
    typeof upstream_channel_id !== 'string' ||
    (upstream_channel_slug !== null && typeof upstream_channel_slug !== 'string') ||
    typeof enabled !== 'boolean'
  ) {
    return null;
  }
  return {
    scope: scope as KeyChannelBindingCascadeSkuTargetImpact['scope'],
    id, sku_id, sku_name, vendor, model, capability_id, upstream_channel_id, upstream_channel_slug, enabled,
  };
}
